import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { App } from 'antd';
import { createTenant } from '../api/tenant';
import type { CreateTenantInput } from '../api/tenant';
import { Topbar } from '../layouts/Topbar';
import { TermChips } from '../components/TermChips';

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function formatDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const TODAY = formatDateInputValue(new Date());

function addMonthsStr(yyyymmdd: string, months: number): string {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const date = new Date(y, m - 1 + months, d);
  if (date.getDate() < d) date.setDate(0);
  return formatDateInputValue(date);
}

// HT-YYYY-MM-DD-HHmmssSSS：年月日取合同起始日，时分秒毫秒取生成时刻
function genContractNo(startAt: string): string {
  const now = new Date();
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  const t = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${pad(now.getMilliseconds(), 3)}`;
  return `HT-${startAt}-${t}`;
}

interface FormState {
  code: string;
  displayName: string;
  plan: string;
  contractStartAt: string;
  contractEndAt: string;
  graceDays: string;
  suspendDays: string;
  purgeDays: string;
  contractNo: string;
  adminEmail: string;
  adminPhone: string;
  adminPassword: string;
  adminPasswordConfirm: string;
}

function initForm(): FormState {
  return {
    code: '',
    displayName: '',
    plan: 'pro',
    contractStartAt: TODAY,
    contractEndAt: addMonthsStr(TODAY, 12),
    graceDays: '7',
    suspendDays: '30',
    purgeDays: '90',
    contractNo: '',
    adminEmail: '',
    adminPhone: '',
    adminPassword: '',
    adminPasswordConfirm: '',
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\- ]{5,32}$/;

const CODE_RE = /^[a-z][a-z0-9-]{2,30}$/;

export function TenantCreatePage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form, setForm] = useState<FormState>(initForm);
  const contractNoTouchedRef = useRef(false);

  // 初始合同号
  useEffect(() => {
    setForm((prev) => ({ ...prev, contractNo: genContractNo(prev.contractStartAt) }));
  }, []);

  const mutate = useMutation({
    mutationFn: (body: CreateTenantInput) => createTenant(body),
    onSuccess: (data) => {
      message.success('租户创建成功，正在异步开库');
      navigate(`/tenants/${data.tenantId}`, { replace: true });
    },
    onError: (e: unknown) => {
      message.error(e instanceof Error ? e.message : '创建失败，请重试');
    },
  });

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'contractNo') contractNoTouchedRef.current = true;
  }

  function handleTermChange({ startAt, endAt }: { startAt: string; endAt: string }) {
    setForm((prev) => {
      const next = { ...prev, contractStartAt: startAt, contractEndAt: endAt };
      if (!contractNoTouchedRef.current && startAt !== prev.contractStartAt) {
        next.contractNo = genContractNo(startAt);
      }
      return next;
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!CODE_RE.test(form.code)) {
      message.error('租户 code 格式不合法：小写字母/数字/中横线，3-30 位');
      return;
    }
    if (!form.displayName.trim()) { message.error('请填写显示名称'); return; }
    if (!form.graceDays.trim() || Number(form.graceDays) < 0) { message.error('请填写宽限期天数'); return; }
    if (!form.suspendDays.trim() || Number(form.suspendDays) < 0) { message.error('请填写停服延迟天数'); return; }
    if (!form.purgeDays.trim() || Number(form.purgeDays) < 0) { message.error('请填写清退延迟天数'); return; }
    if (!form.contractNo.trim()) { message.error('请填写合同号'); return; }
    if (!form.adminPhone.trim()) { message.error('请填写管理员手机号'); return; }
    if (!PHONE_RE.test(form.adminPhone.trim())) { message.error('手机号格式不合法'); return; }
    if (!form.adminEmail.trim()) { message.error('请填写管理员邮箱'); return; }
    if (!EMAIL_RE.test(form.adminEmail.trim())) { message.error('邮箱格式不合法'); return; }
    if (form.adminPassword.length < 12) { message.error('初始密码至少 12 位'); return; }
    if (form.adminPassword !== form.adminPasswordConfirm) { message.error('两次输入的密码不一致'); return; }

    mutate.mutate({
      code: form.code,
      displayName: form.displayName,
      plan: form.plan,
      contractStartAt: form.contractStartAt,
      contractEndAt: form.contractEndAt,
      graceDays: Number(form.graceDays),
      suspendDays: Number(form.suspendDays),
      purgeDays: Number(form.purgeDays),
      contractNo: form.contractNo || null,
      adminPassword: form.adminPassword,
      adminEmail: form.adminEmail,
      adminPhone: form.adminPhone,
    });
  }

  return (
    <>
      <Topbar crumbs={[{ label: '运营' }, { label: '租户管理', href: '/tenants' }, { label: '新建租户' }]} />
      <div className="page">
        <div className="page-header">
          <div className="page-header__title">
            <h1>新建租户</h1>
            <div className="page-header__sub">提交后异步建库（CREATE DATABASE + 灌 schema + 注入 admin），通常 5-15 秒完成。</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="flex flex-col gap-6">
            {/* ① 基本信息 */}
            <div className="card">
              <div className="card__header">
                <div className="card__title">① 基本信息</div>
                <span className="text-xs text-muted">租户 code 决定子域名，全平台唯一且不可修改</span>
              </div>
              <div className="card__body">
                <div className="fieldset">
                  <div className="field-row" style={{ gridTemplateColumns: '2fr 3fr' }}>
                    <div className="field">
                      <label className="field__label" htmlFor="code">租户 code<span className="req">*</span></label>
                      <div className="input-with-suffix">
                        <input id="code" className="input" placeholder="acme" value={form.code}
                          onChange={(e) => set('code', e.target.value)} />
                        <span className="input-suffix">.owlsome.kzdatong.com</span>
                      </div>
                      <div className="field__hint">小写字母/数字/中横线，3-30 字符</div>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="displayName">显示名称<span className="req">*</span></label>
                      <input id="displayName" className="input" placeholder="Acme Industrial" value={form.displayName}
                        onChange={(e) => set('displayName', e.target.value)} />
                    </div>
                  </div>
                  <div className="field-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="field">
                      <label className="field__label" htmlFor="plan">套餐<span className="req">*</span></label>
                      <select id="plan" className="select" value={form.plan}
                        onChange={(e) => set('plan', e.target.value)}>
                        <option value="starter">Starter</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ② 合同与窗口期 */}
            <div className="card">
              <div className="card__header">
                <div className="card__title">② 合同与窗口期</div>
                <span className="text-xs text-muted">到期 → 宽限 → 只读 → 停服 → 清退</span>
              </div>
              <div className="card__body">
                <div className="fieldset">
                  <TermChips
                    startAt={form.contractStartAt}
                    endAt={form.contractEndAt}
                    onChange={handleTermChange}
                    defaultMonths={12}
                  />

                  <div className="field-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <div className="field">
                      <label className="field__label" htmlFor="graceDays">宽限期<span className="req">*</span></label>
                      <div className="input-with-suffix">
                        <input id="graceDays" className="input" type="number" min={0} required value={form.graceDays}
                          onChange={(e) => set('graceDays', e.target.value)} />
                        <span className="input-suffix">天</span>
                      </div>
                      <div className="field__hint">到期 → 只读</div>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="suspendDays">停服延迟<span className="req">*</span></label>
                      <div className="input-with-suffix">
                        <input id="suspendDays" className="input" type="number" min={0} required value={form.suspendDays}
                          onChange={(e) => set('suspendDays', e.target.value)} />
                        <span className="input-suffix">天</span>
                      </div>
                      <div className="field__hint">只读 → 停服</div>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="purgeDays">清退延迟<span className="req">*</span></label>
                      <div className="input-with-suffix">
                        <input id="purgeDays" className="input" type="number" min={0} required value={form.purgeDays}
                          onChange={(e) => set('purgeDays', e.target.value)} />
                        <span className="input-suffix">天</span>
                      </div>
                      <div className="field__hint">停服 → DROP DATABASE</div>
                    </div>
                  </div>

                  <div className="field">
                    <label className="field__label" htmlFor="contractNo">合同号<span className="req">*</span></label>
                    <input id="contractNo" className="input" placeholder="HT-YYYY-MM-DD-HHmmssSSS" required
                      value={form.contractNo} onChange={(e) => set('contractNo', e.target.value)} />
                    <div className="field__hint">默认按合同起始日自动生成，可手动修改</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ③ 初始管理员 */}
            <div className="card">
              <div className="card__header">
                <div className="card__title">③ 初始管理员</div>
                <span className="text-xs text-muted">系统默认账号 admin（系统管理员），仅密码/邮箱/手机号需录入</span>
              </div>
              <div className="card__body">
                <div className="fieldset">
                  <div className="field-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="field">
                      <label className="field__label" htmlFor="adminPhone">手机号<span className="req">*</span></label>
                      <input id="adminPhone" className="input" type="tel" placeholder="13800000000" value={form.adminPhone}
                        onChange={(e) => set('adminPhone', e.target.value)} />
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="adminEmail">邮箱<span className="req">*</span></label>
                      <input id="adminEmail" className="input" type="email" placeholder="admin@acme.com" value={form.adminEmail}
                        onChange={(e) => set('adminEmail', e.target.value)} />
                    </div>
                  </div>
                  <div className="field-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="field">
                      <label className="field__label" htmlFor="adminPassword">初始密码<span className="req">*</span></label>
                      <input id="adminPassword" className="input" type="password" value={form.adminPassword}
                        onChange={(e) => set('adminPassword', e.target.value)} />
                      <div className="field__hint">12 位以上，含大小写数字符号</div>
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="adminPasswordConfirm">确认密码<span className="req">*</span></label>
                      <input id="adminPasswordConfirm" className="input" type="password" value={form.adminPasswordConfirm}
                        onChange={(e) => set('adminPasswordConfirm', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bar">
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/tenants')}>取消</button>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span className="text-xs text-muted">提交后将创建 provision 异步任务</span>
              <button type="submit" className="btn btn-primary" disabled={mutate.isPending}>
                {mutate.isPending ? '提交中…' : '创建并开库'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
